import { Component, inject, OnDestroy, viewChild } from '@angular/core';
import { SpinnerService } from "../../../../shared/spinner/spinner.service";
import { ApiService } from "../../../../services/api.service";
import { AlertService } from "../../../../shared/alert/alert.service";
import { ActivatedRoute, Router } from "@angular/router";
import { NavItemLabelIds, RoutePath } from "../../../../enums/api.enum";
import { WorkflowDetailComponent } from "./workflow-detail/workflow-detail.component";
import { WorkflowInstructionsComponent } from "./workflow-instructions/workflow-instructions.component";
import { MatButtonModule } from "@angular/material/button";

import { MatIconModule } from "@angular/material/icon";
import { UtilitiesService } from "../../../../services/utilities.service";
import { AlertEnum } from "../../../../shared/alert/alert.enum";
import { UtilityStepperComponent } from "../../../../shared/utility-stepper/utility-stepper.component";
import {
  detailSectionIcon,
  fileHeaderIcon,
  gearsIcon,
  myspaceIcon,
  shapesIcon,
  lockIcon,
  SVGIcon
} from "@progress/kendo-svg-icons";
import { MatTooltipModule } from "@angular/material/tooltip";
import { WorkflowBuilderService } from "./services/workflow-builder.service";
import { WorkflowSummaryComponent } from "./workflow-summary/workflow-summary.component";
import { WorkflowSecurityComponent } from "./workflow-security/workflow-security.component";
import { WorkflowAdminComponent } from "./workflow-admin/workflow-admin.component";
import { ProfileService } from "../../../../services/profile.service";
import { AutomationAction, AutomationType, WorkflowStep } from "../../enums/automation.enum";
import { WORKFLOW_STEP_SLUG_MAP, WORKFLOW_STEP_TO_SLUG } from "../../constants/automation-step-slugs.constant";
import { IWorkflow, IWorkflowDetails } from "../../interfaces/i-workflow";
import { AutomationUtilityService } from "../../services/automation-utility.service";
import { IUserConfig } from "../../../../interfaces/i-user-profile";
import { PromptLibraryService } from '../../../ai-chat/services/prompt-library.service';
import { UiLayoutService } from "../../../../ui-layout/services/ui-layout.service";
import { catchError, EMPTY, switchMap, tap, throwError } from "rxjs";
import { PrevalidationWorkflowExecutionResponse } from "../../../../interfaces/responses";
import { resolveStepSlugIndex } from "../../../../utilities/step-slug";

/**
 * WorkflowBuilderComponent is responsible for managing the creation, editing, and cloning of workflows.
 * It coordinates the workflow builder steps, handles form validation, and communicates with backend services
 * to persist workflow data. The component also manages navigation between steps and user feedback via alerts.
 */
@Component({
  selector: 'app-workflow-builder',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    WorkflowDetailComponent,
    WorkflowInstructionsComponent,
    WorkflowSummaryComponent,
    UtilityStepperComponent,
    WorkflowSecurityComponent,
    WorkflowAdminComponent
],
  providers: [
    AutomationUtilityService
  ],
  templateUrl: './workflow-builder.component.html',
  styleUrl: './workflow-builder.component.scss'
})
export class WorkflowBuilderComponent implements OnDestroy {
  workflowDetailComponent = viewChild(WorkflowDetailComponent);
  workflowInstructionsComponent = viewChild(WorkflowInstructionsComponent);
  workflowSecurityComponent = viewChild(WorkflowSecurityComponent);
  workflowAdminComponent = viewChild(WorkflowAdminComponent);
  title = 'Create Workflow';
  workflowId: string | null = null;
  // Action to perform (create, edit, clone)
  buildAction: string = RoutePath.CreateWorkflow;
  // Current step in the workflow builder as a signal
  currentStep = 0;
  // Flag to control validation
  validateOnRender = false;
  userProfileConfig: IUserConfig;

  readonly detailSectionIcon: SVGIcon = detailSectionIcon;
  readonly instructionsIcon: SVGIcon = shapesIcon;
  readonly summaryIcon: SVGIcon = fileHeaderIcon;
  readonly securityIcon: SVGIcon = lockIcon;
  readonly adminIcon: SVGIcon = myspaceIcon;
  readonly configIcon: SVGIcon = gearsIcon;
  readonly workflowStep = WorkflowStep;

  steps = [
    {
      label: WorkflowStep.DETAILS,
      // iconClass: 'custom-network-icon',
      svgIcon: this.detailSectionIcon,
      // Function to check if the step is valid, only when validation is requested
      isValid: (): boolean => this.validateOnRender ? this.isStepValid(WorkflowStep.DETAILS) : true,
      validate: true,
    },
    {
      label: WorkflowStep.INSTRUCTIONS,
      // iconClass: 'custom-atr-icon',
      svgIcon: this.instructionsIcon,
      isValid: (): boolean => this.validateOnRender ? this.isStepValid(WorkflowStep.INSTRUCTIONS) : true,
      validate: true,
    },
    {
      label: WorkflowStep.SECURITY,
      // iconClass: 'custom-atr-icon',
      svgIcon: this.securityIcon,
      isValid: (): boolean => this.validateOnRender ? this.isStepValid(WorkflowStep.SECURITY) : true,
      validate: true,
    },
    {
      label: WorkflowStep.SUMMARY,
      // iconClass: 'custom-atr-icon',
      svgIcon: this.summaryIcon,
      isValid: (): boolean => true, // Summary step doesn't require validation
      validate: true
    },
  ];

  #errorWhileSavingWorkflow = false;

  #spinnerService = inject(SpinnerService);
  #apiService = inject(ApiService);
  #alertService = inject(AlertService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #utilitiesService = inject(UtilitiesService);
  #workflowBuilderService = inject(WorkflowBuilderService);
  #profileService = inject(ProfileService);
  #automationUtilityService = inject(AutomationUtilityService);
  #promptLibraryService = inject(PromptLibraryService);
  #uiLayoutService = inject(UiLayoutService);

  constructor() {
    this.userProfileConfig = this.#profileService.userProfile()?.config!;

    this.#route.params.subscribe(params => {
      if (params['workflowId']) {
        // Get the workflow ID from the URL
        this.workflowId = params['workflowId'];
        this.buildAction = this.#router.url.split('/')[1];
        this.title = (this.buildAction === RoutePath.EditWorkflow) ? 'Edit Workflow' : 'Clone Workflow';

        // Only fetch from the API if this workflow isn't already loaded in the service.
        if (this.#workflowBuilderService.workflow().id !== this.workflowId) {
          this.#spinnerService.show();
          this.#apiService.getWorkflow(this.workflowId!).subscribe({
            next: workflow => {
              if (workflow) {
                this.#workflowBuilderService.workflow.set(workflow);
              } else {
                this.#alertService.showAlert({
                  message: `Workflow not found.`,
                  type: AlertEnum.error,
                });
              }
              this.#spinnerService.hide();
            },
            error: (error: any) => {
              this.#spinnerService.hide();
              this.#alertService.showAlert({
                message: `Failed to get the workflow.`,
                type: AlertEnum.error,
              });
            }
          });
        }
      }

      // Resolve the initial step from the optional :step URL segment.
      // Only applicable for edit routes (edit-workflow/:id/:step).
      if (params['workflowId'] && this.buildAction === RoutePath.EditWorkflow) {
        const index = resolveStepSlugIndex(params['step'], WORKFLOW_STEP_SLUG_MAP, this.steps);
        if (index !== -1) this.currentStep = index;
      }
    });

    if (this.userProfileConfig.isAdmin || this.userProfileConfig.canManageWorkflows) {
      // Add the admin step if the user is an admin or has manage workflows permission
      // Find the index of the SECURITY step
      const securityIndex = this.steps.findIndex(step => step.label === WorkflowStep.SECURITY);
      const adminStep = {
        label: WorkflowStep.ADMIN,
        // iconClass: 'custom-atr-icon',
        svgIcon: this.adminIcon,
        isValid: (): boolean => this.validateOnRender ? this.isStepValid(WorkflowStep.ADMIN) : true,
        validate: true,
      };
      // Insert the new step after SECURITY and before SUMMARY
      if (securityIndex !== -1) {
        this.steps.splice(securityIndex + 1, 0, adminStep);
      }
    }
  }

  /**
   * Navigates the user back to their previous location.
   * Typically used to cancel the workflow creation or editing process.
   */
  onCancel() {
    this.#utilitiesService.goToLastLocation();
  }

  ngOnDestroy(): void {
    // Only reset if navigating away from the workflow builder entirely.
    // When stepping between steps the component is destroyed/recreated but
    // the target URL still contains the builder path — preserve the signal.
    const targetUrl = this.#router.getCurrentNavigation()?.finalUrl?.toString() ?? '';
    const stayingInBuilder =
      targetUrl.includes(RoutePath.EditWorkflow) ||
      targetUrl.includes(RoutePath.CloneWorkflow) ||
      targetUrl.includes(RoutePath.CreateWorkflow);

    if (!stayingInBuilder) {
      this.#workflowBuilderService.resetWorkflow();
    }
  }

  /**
   * Resets the workflow instruction form to its default values.
   * Optionally displays a success alert after resetting.
   *
   * @param showAlert Whether to show an alert after resetting (default: true).
   */
  onReset() {
    this.#reset();
  }

  /**
   * Initiates the save operation for the current workflow.
   * Persists the workflow state using the workflow builder service.
   */
  onSave() {
    if (!this.validateAllChildForms()) {
      // If validation fails, do not proceed with saving
      return;
    }
    this.#updateWorkflow();
    const userProfile = this.#profileService.userProfile();
    const user = {
      createdBy: userProfile?.profile.email,
      modifiedBy: userProfile?.profile.email,
    };

    // Update the workflow state with user info
    this.#workflowBuilderService.workflow.update((workflow) => ({
      ...workflow,
      ...user,
      details: {
        // Ensure all required fields are set and not undefined
        name: workflow.details?.name ?? '',
        description: workflow.details?.description ?? '',
        isEnabled: workflow.details?.isEnabled ?? true,
        isUniversal: workflow.details?.isUniversal ?? false,
        sensitivityLabels: workflow.details?.sensitivityLabels ?? [],
        owners: workflow.details?.owners ?? [],
        users: workflow.details?.users ?? [],
        groups: workflow.details?.groups ?? [],
        instructions: workflow.details?.instructions ?? [],
        keywords: workflow.details?.keywords ?? [],
        isAdvertised: workflow.details?.isAdvertised ?? false,
        isAutoApproved: workflow.details?.isAutoApproved ?? false,
        notifyAutoApproval: workflow.details?.notifyAutoApproval ?? false,
      }
    }));
    // Now set the request body with the current workflow state
    const requestBody = this.#workflowBuilderService.workflow().details;

    if (this.buildAction === RoutePath.EditWorkflow) {
      const workflowId = this.#workflowBuilderService.workflow().id;
      this.editWorkflow(workflowId!, requestBody!);
    } else {
      if (this.#errorWhileSavingWorkflow) {
        // If we are cloning or creating and there was an error while saving, we need to edit the workflow instead of creating a new one.
        // This is because we save a workflow first before validating it.  If there was an error, the workflow already exists.
        // So we need to edit the existing workflow instead of creating a new one.
        // This allows the user to correct the error and save the existing workflow.
        const workflowId = this.#workflowBuilderService.workflow().id;
        this.editWorkflow(workflowId!, requestBody!);
      } else {
        this.createWorkflow(requestBody!);
      }
    }
  }

  /**
   * Creates a new workflow by sending the workflow details to the API.
   *
   * @param requestBody - The workflow details to be sent to the API for creation.
   */
  createWorkflow(requestBody: IWorkflowDetails): void {
    this.#spinnerService.show();

    this.#apiService.createWorkflow(requestBody).pipe(
      tap((response: IWorkflow) => {
        // A simple type guard to check for the error property
        if ('error' in response) {
          // If the error property exists, throw a client-side error
          throw new Error('Unknown creation error');
        }
        // Set the signal here, where you first receive the response
        this.#workflowBuilderService.workflow.set(response);
      }),
      switchMap((createResp: IWorkflow) =>
        this.#apiService.prevalidateWorkflowExecution(createResp.id!)
      ),
      // Handle the custom error case for prevalidateExecution
      tap((prevalidateResp: PrevalidationWorkflowExecutionResponse) => {
        if (!prevalidateResp.success) {
          this.#errorWhileSavingWorkflow = true;
          throw new Error(prevalidateResp.error?.message || 'Unknown prevalidation error');
        }
      }),
      // Catch any errors thrown by the tap operators or underlying HTTP calls
      catchError((error) => {
        return throwError(() => error); // Halts further execution. This will cause the subscribe's error block to be executed.
      })
    ).subscribe({
      next: resp => {
        this.#spinnerService.hide();
        this.#alertService.showAlert({
          message: `Workflow saved successfully.`,
          type: AlertEnum.success,
        });

        this.#errorWhileSavingWorkflow = false;
        // Reload workflows in the Automation Library
        this.#promptLibraryService.loadWorkflows();

        // Redirect to the workflow table after successful creation
        this.#automationUtilityService.routeToTable(AutomationType.WORKFLOW, AutomationAction.MY_ITEMS);
      },
      error: (error: any) => {
        this.#spinnerService.hide();
        let errorMessage = `Failed to save workflow.`;
        // Check if it's a custom validation error
        if (error && error.name === 'Error' && typeof error.message === 'string') {
          errorMessage = error.message; // Use the specific message you created
        }
        // Optional: Add handling for other specific HTTP errors if needed
        // else if (error instanceof HttpErrorResponse) {
        //   errorMessage = `Server error: ${error.statusText}`;
        // }

        this.#alertService.showAlert({
          message: errorMessage,
          type: AlertEnum.error,
          duration: 12
        });
      }
    });
  }

  /**
   * Updates an existing workflow by sending the updated workflow details to the API.
   *
   * @param id - The unique identifier of the workflow to update.
   * @param requestBody - The updated workflow details to be sent to the API.
   */
  editWorkflow(id: string, requestBody: IWorkflowDetails): void {
    this.#spinnerService.show();
    this.#apiService.editWorkflow(id, requestBody).pipe(
      tap((response: IWorkflow) => {
        // A simple type guard to check for the error property
        if ('error' in response) {
          // If the error property exists, throw a client-side error
          throw new Error('Unknown creation error');
        }
      }),
      switchMap((createResp: IWorkflow) =>
        this.#apiService.prevalidateWorkflowExecution(createResp.id!)
      ),
      // Handle the custom error case for prevalidateExecution
      tap((prevalidateResp: PrevalidationWorkflowExecutionResponse) => {
        if (!prevalidateResp.success) {
          this.#errorWhileSavingWorkflow = true;
          throw new Error(prevalidateResp.error?.message || 'Unknown prevalidation error');
        }
      }),
      // Catch any errors thrown by the tap operators or underlying HTTP calls
      catchError((error) => {
        return throwError(() => error); // Halts further execution. This will cause the subscribe's error block to be executed.
      })
    ).subscribe({
      next: resp => {
        this.#spinnerService.hide();
        this.#alertService.showAlert({
          message: `Workflow saved successfully.`,
          type: AlertEnum.success,
        });

        this.#errorWhileSavingWorkflow = false;
        // Reload workflows in the Automation Library
        this.#promptLibraryService.loadWorkflows();

        // If there is no last route or current route is create-workflow, then take the route to the My Workflow page after saving a Workflow.
        // Select the NavItem for My Workflow on the left nav menu.
        // If there is no meaningful last route, redirect to My Workflows.
        // "Not meaningful" means: empty, the app-load default (/start-conversation),
        // or the user opened a builder URL directly (edit/clone/create-workflow).
        const noMeaningfulOrigin = this.#utilitiesService.isNoMeaningfulOrigin(
          RoutePath.EditWorkflow,
          RoutePath.CloneWorkflow,
          RoutePath.CreateWorkflow
        );

        if (noMeaningfulOrigin) {
          this.#uiLayoutService.setSelectedNavItem(NavItemLabelIds.MyWorkflows);
          this.#utilitiesService.locationFrom = `/${RoutePath.MyWorkflows}`;
          this.#automationUtilityService.routeToTable(AutomationType.WORKFLOW, AutomationAction.MY_ITEMS, this.#utilitiesService.locationFrom);
        }

        const selectedNavItem = this.#uiLayoutService.getSelectedNavItem(this.#utilitiesService.locationFrom.slice(1));

        this.#uiLayoutService.setSelectedNavItem(selectedNavItem?.id!);

        this.#router.navigateByUrl(this.#utilitiesService.locationFrom); // Redirect to last location
      },
      error: (error: any) => {
        this.#spinnerService.hide();
        let errorMessage = `Failed to save workflow.`;
        // Check if it's a custom validation error
        if (error && error.name === 'Error' && typeof error.message === 'string') {
          errorMessage = error.message; // Use the specific message you created
        }
        // Optional: Add handling for other specific HTTP errors if needed
        // else if (error instanceof HttpErrorResponse) {
        //   errorMessage = `Server error: ${error.statusText}`;
        // }

        this.#alertService.showAlert({
          message: errorMessage,
          type: AlertEnum.error,
          duration: 12
        });
      }
    });
  }

  /**
   * Check if the step form is valid
   * @param step The step to check
   * @returns A boolean indicating if the step form is valid
   */
  isStepValid(step: string): boolean {
    // NOTE: We don't need to validate the admin step because it consists of checkboxes which always has a value
    switch (step) {
      case WorkflowStep.DETAILS:
        return this.workflowDetailComponent()?.isValid() ?? false;
      case WorkflowStep.INSTRUCTIONS:
        return this.workflowInstructionsComponent()?.isValid() ?? false;
      case WorkflowStep.SECURITY:
        return this.workflowSecurityComponent()?.isValid() ?? false;
      default:
        return true;
    }
  }

  /**
   * Handles step change in the stepper
   * @param index Numeric index of the selected step (0: Definition, 1: Types, 2: Admin)
   */
  onStepChange(index: number): void {
    // Update workflow with current form values so they are saved before changing step
    this.#updateWorkflow();

    // The step change will only happen if onStepActivate returned true
    // So we can safely update the current step
    this.currentStep = index;

    // Sync the URL step slug so the active step is bookmarkable / shareable.
    this.#syncStepToUrl(index);
  }

  /**
   * Updates the URL to reflect the currently active step slug, enabling bookmarking and sharing.
   * Replaces the `:step` segment without adding a new browser history entry.
   */
  #syncStepToUrl(index: number): void {
    // Step-slug URLs are only supported for edit routes (edit-workflow/:id/:step).
    if (!this.workflowId || this.buildAction !== RoutePath.EditWorkflow) {
      return;
    }

    const stepLabel = this.steps[index]?.label as WorkflowStep | undefined;
    const slug = stepLabel ? WORKFLOW_STEP_TO_SLUG[stepLabel] : undefined;
    if (!slug) {
      return;
    }

    const urlSegments = this.#router.url.split('/');
    // urlSegments: ['', 'edit-workflow', ':id', ':step?']
    // Replace or append the step slug at index 3.
    urlSegments[3] = slug;

    this.#router.navigateByUrl(urlSegments.join('/'), { replaceUrl: true });
  }

  /**
   * Handles the activate event from Kendo stepper
   * This method is called before the step is changed and can prevent navigation
   * @param event The event containing the requested step index
   */
  onStepActivate(event: any) {
    // Enable validation only when needed for navigation
    this.validateOnRender = true;

    // Validate the current step
    const isCurrentStepValid = this.#validateCurrentStep();

    if (!isCurrentStepValid) {
      // Show validation errors
      this.#alertService.showAlert({
        message: `Please correct the validation errors before proceeding.`,
        type: AlertEnum.error,
      });

      // Prevent navigation by returning false
      event.preventDefault();
    }
  }

  /**
   * Checks each child form in order. If a form is invalid, sets currentStep to that step,
   * shows an alert, and returns false. If all are valid, returns true.
   */
  validateAllChildForms(): boolean {
    // There is a chance that the user has filled out steps 1 & 2, but not 3 & 4 and then navigated to the summary.
    // This is possible because steps 3 & 4 have default values.  The problem is, if the process bypasses step 3,
    // the default value is not set in the workflow, only in the form.  We need to ensure that the workflow has the owner's
    // default value set which is the current user.
    // Check if the workflow has an owner set, if not, set it to the current user.
    // We also need to do the same for the isEnabled and isUniversal properties.
    // These properties could be undefined if the user has not set them in the form.
    const currentWorkflow = this.#workflowBuilderService.workflow();

    // Ensure isEnabled and isUniversal are booleans and owners is a non-empty array; if not, set defaults
    if (
      typeof currentWorkflow.details?.isEnabled !== 'boolean' ||
      typeof currentWorkflow.details?.isUniversal !== 'boolean' ||
      !currentWorkflow.details?.owners ||
      currentWorkflow.details.owners.length === 0
    ) {
      this.#workflowBuilderService.setDefaultSecurityAndAdminValues();
    }

    // Define the steps to validate, each with its component, validation check, and alert message
    const steps = [
      {
        component: this.workflowDetailComponent,
        serviceCheck: () => {
          // Check if details (name, description, sensitivityLabels) are present and valid
          const details = this.#workflowBuilderService.workflow().details;
          // Set showSensitivityLabelValidation to whether sensitivityLabels is non-empty
          // this.showSensitivityLabelValidation = Array.isArray(details?.sensitivityLabels) && details.sensitivityLabels.length > 0;
          return details?.name && details?.description;
        },
        alertMsg: 'Please correct the errors in the Details step before proceeding.'
      },
      {
        component: this.workflowInstructionsComponent,
        serviceCheck: () => {
          // Check if at least one instruction is selected
          const instructions = this.#workflowBuilderService.workflow().details?.instructions;
          return Array.isArray(instructions) && instructions.length > 0;
        },
        alertMsg: 'Please select at least one instruction in the Instructions step before proceeding.'
      },
      {
        component: this.workflowSecurityComponent,
        serviceCheck: () => {
          // Check if at least one owner is selected
          const owners = this.#workflowBuilderService.workflow().details?.owners;
          return Array.isArray(owners) && owners.length > 0;
        },
        alertMsg: 'Please correct the errors in the Security step before proceeding.'
      },
      {
        component: this.workflowAdminComponent,
        serviceCheck: () => {
          // Check if isEnabled and isUniversal is a boolean
          const details = this.#workflowBuilderService.workflow().details;
          return typeof details?.isEnabled === 'boolean' && typeof details?.isUniversal === 'boolean';
        },
        alertMsg: 'Please correct the errors in the Security step before proceeding.'
      }
    ];

    // Iterate through each step and validate
    for (let i = 0; i < steps.length; i++) {
      const { component, serviceCheck, alertMsg } = steps[i];
      // If the form is invalid and the service check fails, show alert and set current step
      if (!component()?.isValid?.()) {
        if (!serviceCheck()) {
          this.currentStep = i;
          component()?.markFormAsTouched(); // Mark all controls as touched to show validation errors
          this.#alertService.showAlert({ message: alertMsg, type: AlertEnum.error });
          return false;
        }
      }
    }
    // All steps are valid
    return true;
  }

  /**
   * Reset the instruction form to default values
   * @param showAlert Whether to show an alert after resetting
   */
  #reset(showAlert = true): void {
    // Clear workflow and instructions data
    this.#workflowBuilderService.workflow.set({});
    this.#workflowBuilderService.instructions.set([]);
    this.currentStep = 0; // Reset to the first step

    // Delay resetting child forms until after stepper navigation
    setTimeout(() => {
      this.workflowDetailComponent()?.resetForm();
      this.workflowSecurityComponent()?.resetForm();
      this.workflowAdminComponent()?.resetForm();
    });

    if (showAlert) {
      this.#alertService.showAlert({
        message: 'The workflow form has been reset to default values.',
        type: AlertEnum.success,
      });
    }
  }

  /**
   * Validates the current step before moving to the next
   * @returns boolean indicating if the current step is valid
   */
  #validateCurrentStep(): boolean {
    switch (this.currentStep) {
      case 0:
        return this.isStepValid(WorkflowStep.DETAILS);
      case 1:
        return this.isStepValid(WorkflowStep.INSTRUCTIONS);
      case 2:
        return this.isStepValid(WorkflowStep.SECURITY);
      case 3:
        return this.isStepValid(WorkflowStep.ADMIN);
      default:
        return true;
    }
  }

  /**
   * Synchronizes the workflow state by invoking the `updateWorkflow` method
   * on each child component (details, instructions, security, admin).
   *
   * This ensures that the latest form values from all workflow builder steps
   * are persisted to the shared workflow service before navigation or saving.
   *
   * Called internally when changing steps or saving the workflow.
   */
  async #updateWorkflow() {
    // Call the child component's updateWorkflow to sync form values to the service
    this.workflowDetailComponent()?.updateWorkflow();
    this.workflowInstructionsComponent()?.updateWorkflow();
    this.workflowSecurityComponent()?.updateWorkflow();
    this.workflowAdminComponent()?.updateWorkflow();
  }
}
